import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { playgroundVideosApi } from '../../data/api/playground-videos.api'
import { queueApi } from '../../data/api/queue.api'
import { isPlaygroundPending } from '../models/PlaygroundGeneration'
import type { PlaygroundVideoGeneration, PlaygroundVideoRequest } from '../models/PlaygroundVideo'

import { workspaceMediaItemKeys } from './useWorkspaceMediaItems'

export const playgroundVideoKeys = {
  all: ['playgrounds'] as const,
  workspace: (workspaceId: string) => ['playgrounds', workspaceId, 'video'] as const,
  providers: (workspaceId: string) => ['playgrounds', workspaceId, 'video', 'providers'] as const,
  videos: (workspaceId: string) => ['playgrounds', workspaceId, 'video', 'videos'] as const,
  sources: (workspaceId: string, kind: string | null, provider?: string) =>
    ['playgrounds', workspaceId, 'video', 'sources', kind, provider ?? null] as const,
  active: (workspaceId: string) => ['playgrounds', workspaceId, 'video', 'active'] as const,
  status: (workspaceId: string, activityId: string) =>
    ['playgrounds', workspaceId, 'video', 'status', activityId] as const,
  validate: (workspaceId: string) => ['playgrounds', workspaceId, 'video', 'validate'] as const,
  generate: (workspaceId: string) => ['playgrounds', workspaceId, 'video', 'generate'] as const,
}

export type TrackedVideoActivity = PlaygroundVideoGeneration

export function usePlaygroundVideos(workspaceId: string, enabled: boolean) {
  const queryClient = useQueryClient()
  const completed = useRef(new Set<string>())
  const submittedMetadata = useRef(
    new Map<string, Pick<TrackedVideoActivity, 'capability' | 'provider'>>(),
  )
  const [trackedIds, setTrackedIds] = useState<string[]>([])
  const [lastResults, setLastResults] = useState<PlaygroundVideoGeneration[]>([])
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(() => new Set())

  const videos = useQuery({
    queryKey: playgroundVideoKeys.videos(workspaceId),
    queryFn: () => playgroundVideosApi.videos(workspaceId).then((response) => response.data),
    enabled,
  })
  const active = useQuery({
    queryKey: playgroundVideoKeys.active(workspaceId),
    queryFn: () => playgroundVideosApi.active(workspaceId).then((response) => response.data),
    enabled,
    refetchOnMount: 'always',
    refetchInterval: 10000,
  })
  const validation = useMutation({
    mutationKey: playgroundVideoKeys.validate(workspaceId),
    mutationFn: (request: PlaygroundVideoRequest) =>
      playgroundVideosApi.validate(workspaceId, request).then((response) => response.data),
  })
  const submission = useMutation({
    mutationKey: playgroundVideoKeys.generate(workspaceId),
    mutationFn: (request: PlaygroundVideoRequest) =>
      playgroundVideosApi.generate(workspaceId, request).then((response) => response.data),
    onSuccess: (accepted, request) => {
      submittedMetadata.current.set(accepted.activityId, {
        capability: request.capability,
        provider: request.videoProvider,
      })
      setTrackedIds((previous) =>
        previous.includes(accepted.activityId) ? previous : [...previous, accepted.activityId],
      )
      void queryClient.invalidateQueries({ queryKey: playgroundVideoKeys.active(workspaceId) })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: playgroundVideoKeys.active(workspaceId) })
    },
  })
  const cancellation = useMutation({
    mutationFn: (activityId: string) =>
      queueApi.cancel(activityId).then((response) => response.data),
    onMutate: (activityId) => {
      setCancellingIds((previous) => new Set(previous).add(activityId))
    },
    onSuccess: (_data, activityId) => {
      void queryClient.invalidateQueries({
        queryKey: playgroundVideoKeys.status(workspaceId, activityId),
      })
      void queryClient.invalidateQueries({ queryKey: playgroundVideoKeys.active(workspaceId) })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
    onSettled: (_data, _error, activityId) => {
      setCancellingIds((previous) => {
        const next = new Set(previous)
        next.delete(activityId)
        return next
      })
    },
  })

  useEffect(() => {
    if (!active.data) {
      return
    }
    setTrackedIds((previous) => {
      const next = new Set(previous)
      for (const activity of active.data) {
        if (isPlaygroundPending(activity.status) && !completed.current.has(activity.activityId)) {
          next.add(activity.activityId)
        }
      }
      return [...next]
    })
  }, [active.data])

  const statuses = useQueries({
    queries: trackedIds.map((activityId) => ({
      queryKey: playgroundVideoKeys.status(workspaceId, activityId),
      queryFn: () =>
        playgroundVideosApi.status(workspaceId, activityId).then((response) => response.data),
      enabled,
      refetchInterval: (query: { state: { data: PlaygroundVideoGeneration | undefined } }) =>
        query.state.data && !isPlaygroundPending(query.state.data.status) ? false : 10000,
    })),
  })

  useEffect(() => {
    for (const query of statuses) {
      const activity = query.data
      if (
        !activity ||
        isPlaygroundPending(activity.status) ||
        completed.current.has(activity.activityId)
      ) {
        continue
      }
      completed.current.add(activity.activityId)
      setLastResults((previous) => [
        activity,
        ...previous.filter((result) => result.activityId !== activity.activityId),
      ])
      setTrackedIds((previous) => previous.filter((id) => id !== activity.activityId))
      void queryClient.invalidateQueries({ queryKey: playgroundVideoKeys.videos(workspaceId) })
      void queryClient.invalidateQueries({ queryKey: playgroundVideoKeys.active(workspaceId) })
      void queryClient.invalidateQueries({
        queryKey: workspaceMediaItemKeys.workspace(workspaceId),
      })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    }
  }, [queryClient, statuses, workspaceId])

  const activities = useMemo(
    () =>
      statuses.flatMap((query) => {
        if (!query.data || !isPlaygroundPending(query.data.status)) {
          return []
        }
        const metadata = submittedMetadata.current.get(query.data.activityId)
        return [{ ...metadata, ...query.data }]
      }),
    [statuses],
  )

  return {
    videos,
    active,
    activities,
    lastResults,
    validation,
    submission,
    cancellation,
    cancellingIds,
    statusError: statuses.some((query) => query.isError),
    retryStatuses: () => {
      void active.refetch()
      for (const query of statuses) {
        void query.refetch()
      }
    },
  }
}
